import type { FreshnessTier } from "../FreshnessBadge";

/**
 * Pure grouping of search rows (one per unit) into property -> floor plan cards
 * for ApartmentSearch. Extracted from ApartmentSearch.groupResults (PKT-0943) so
 * the prices it feeds the client email / shortlist can be tested directly.
 */
export interface SearchResult {
  property_id: string;
  property_name: string;
  neighborhood?: string;
  model_name?: string | null;
  bedrooms?: number | null;
  bathrooms?: number | null;
  square_feet?: number | null;
  unit_number?: string | null;
  current_price?: number | null;
  status?: string | null;
  price_per_sqft?: number | null;
  availability_date?: string;
  s3_image_url?: string;
  floorplan_image_url?: string;
  floor_plan_id?: string;
  property_website?: string;
  url?: string;
  // `specials` is the raw/short DB value; `specials_summary` is the
  // human-readable summary used for display elsewhere (PropertiesPage,
  // AdminDashboard, MarketInsights). The email build path below
  // prefers specials_summary and falls back to specials.
  specials?: string;
  specials_summary?: string;
  property_management?: string;
  lambda_compatible?: boolean | null;
  latitude?: number;
  longitude?: number;
  distance_miles?: number;
  inventory_contract_version?: string;
  inventory_freshness_state?: FreshnessTier;
  inventory_last_verified_at?: string;
  inventory_serve_pricing?: boolean;
  inventory_freshness_badge?: "green" | "yellow" | "red";
  inventory_serve_note?: string | null;
}

// Grouped data structures
export interface GroupedUnit {
  available_date?: any;
  unit_number: string;
  current_price: number;
  status: string;
  price_per_sqft: number;
  unique_id: string; // Unique identifier for selection - fixes "private" unit issue
}

export interface GroupedFloorPlan {
  model_name: string;
  bedrooms: number;
  bathrooms: number;
  square_feet: number;
  units: GroupedUnit[];
  min_price: number;
  max_price: number;
  available_count: number;
  image_url?: string;
  // DB UUID — used to build LocateNow floorplan-page URLs when the email
  // toggle is on. Optional because historical/cached results may not have it.
  floor_plan_id?: string;
}

export interface SearchGroupedProperty {
  url?: string;
  website?: string;
  // See SearchResult above for the specials vs specials_summary split.
  specials?: string;
  specials_summary?: string;
  neighborhood?: string;
  property_id: string;
  property_name: string;
  floor_plans: GroupedFloorPlan[];
  total_units: number;
  available_units: number;
  price_range: { min: number; max: number };
  bed_range: { min: number; max: number };
  s3_image_url?: string;
  property_management?: string;
  lambda_compatible?: boolean | null;
  is_favorite?: boolean;
  freshness_tier: FreshnessTier;
  freshness_badge: "green" | "yellow" | "red";
  freshness_note?: string | null;
  last_verified_at?: string;
  serve_pricing: boolean;
}

/** Preserve civil search/inventory dates across browser restoration. */
export const parseSearchCalendarDate = (value?: string): Date | null => {
  if (!value) return null;
  const calendar = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(value);
  const date = calendar
    ? new Date(Number(calendar[1]), Number(calendar[2]) - 1, Number(calendar[3]))
    : new Date(value);
  if (!Number.isFinite(date.getTime())) return null;
  if (calendar && (date.getFullYear() !== Number(calendar[1]) ||
    date.getMonth() !== Number(calendar[2]) - 1 || date.getDate() !== Number(calendar[3]))) return null;
  return date;
};

// Helper to check if a unit is available by a target date
export const isAvailableByDate = (
  status: string | null | undefined,
  availabilityDate?: string,
  targetDate?: Date
): boolean => {
  if (status !== "available") return false;
  if (!availabilityDate) return true; // No date means available now

  try {
    const date = parseSearchCalendarDate(availabilityDate);
    if (!date) return false;
    const compareDate = targetDate || new Date();
    // Compare dates only (ignore time)
    const dateOnly = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );
    const compareDateOnly = new Date(
      compareDate.getFullYear(),
      compareDate.getMonth(),
      compareDate.getDate()
    );

    return dateOnly <= compareDateOnly;
  } catch {
    return false; // Malformed date evidence cannot establish availability
  }
};

export function groupSearchResults(
  searchResults: SearchResult[],
  targetDate: Date
): SearchGroupedProperty[] {
  const propertyMap = new Map<string, SearchGroupedProperty>();

  let unitCounter = 0;

  searchResults.forEach((result) => {
    if (!propertyMap.has(result.property_id)) {
      propertyMap.set(result.property_id, {
        property_id: result.property_id,
        property_name: result.property_name || "Unknown Property",
        neighborhood: result.neighborhood,
        website: result.property_website,
        specials: result.specials,
        specials_summary: result.specials_summary,
        floor_plans: [],
        total_units: 0,
        available_units: 0,
        price_range: { min: Infinity, max: 0 },
        bed_range: { min: Infinity, max: 0 },
        s3_image_url: result.s3_image_url,
        property_management: result.property_management,
        lambda_compatible: result.lambda_compatible,
        is_favorite: (result as any).is_favorite || false,
        freshness_tier: result.inventory_freshness_state || "stale",
        freshness_badge: result.inventory_freshness_badge || "red",
        freshness_note: result.inventory_serve_note,
        last_verified_at: result.inventory_last_verified_at,
        serve_pricing: result.inventory_serve_pricing === true,
      });
    }

    const property = propertyMap.get(result.property_id)!;

    if (!property.serve_pricing) {
      return;
    }

    // Update property image if not set
    if (!property.s3_image_url && result.s3_image_url) {
      property.s3_image_url = result.s3_image_url;
    }

    property.total_units++;
    // Prices and availability must describe the same eligible units. Keep all
    // rows for inspection, but never quote leased or later-than-move-in rent.
    const availableByTarget = isAvailableByDate(
      result.status, result.availability_date, targetDate
    );
    const hasRentablePrice = availableByTarget &&
      typeof result.current_price === "number" &&
      Number.isFinite(result.current_price) && result.current_price > 0;
    if (availableByTarget) {
      property.available_units++;
    }

    if (hasRentablePrice && result.current_price != null) {
      property.price_range.min = Math.min(
        property.price_range.min,
        result.current_price
      );
      property.price_range.max = Math.max(
        property.price_range.max,
        result.current_price
      );
    }
    if (result.bedrooms != null && isFinite(result.bedrooms)) {
      property.bed_range.min = Math.min(
        property.bed_range.min,
        result.bedrooms
      );
      property.bed_range.max = Math.max(
        property.bed_range.max,
        result.bedrooms
      );
    }

    let floorPlan = property.floor_plans.find(
      (fp) => fp.model_name === result.model_name
    );
    if (!floorPlan) {
      floorPlan = {
        model_name: result.model_name || "Unknown",
        bedrooms: result.bedrooms ?? 0,
        bathrooms: result.bathrooms ?? 0,
        square_feet: result.square_feet ?? 0,
        units: [],
        min_price: Infinity,
        max_price: 0,
        available_count: 0,
        image_url: result.floorplan_image_url,
        floor_plan_id: result.floor_plan_id,
      };
      property.floor_plans.push(floorPlan);
    }

    // Update floor plan image if not set
    if (!floorPlan.image_url && result.floorplan_image_url) {
      floorPlan.image_url = result.floorplan_image_url;
    }
    if (!floorPlan.floor_plan_id && result.floor_plan_id) {
      floorPlan.floor_plan_id = result.floor_plan_id;
    }

    // Generate unique ID for each unit - fixes "private" unit selection issue
    const uniqueId = `${result.property_id}-${
      result.model_name
    }-${unitCounter++}-${result.current_price}`;

    floorPlan.units.push({
      unit_number: result.unit_number || "N/A",
      current_price: result.current_price ?? 0,
      status: result.status || "unknown",
      price_per_sqft: result.price_per_sqft ?? 0,
      available_date: result.availability_date,
      unique_id: uniqueId,
    });

    if (hasRentablePrice && result.current_price != null) {
      floorPlan.min_price = Math.min(
        floorPlan.min_price,
        result.current_price
      );
      floorPlan.max_price = Math.max(
        floorPlan.max_price,
        result.current_price
      );
    }
    // Only count as available if available by the target move-in date (or today)
    if (availableByTarget) {
      floorPlan.available_count++;
    }
  });

  const grouped = Array.from(propertyMap.values());
  grouped.forEach((property) => {
    property.floor_plans.sort(
      (a, b) =>
        (a.bedrooms ?? 0) - (b.bedrooms ?? 0) ||
        (a.min_price ?? 0) - (b.min_price ?? 0)
    );
    property.floor_plans.forEach((fp) =>
      fp.units.sort((a, b) => (a.current_price ?? 0) - (b.current_price ?? 0))
    );
  });
  grouped.sort((a, b) => {
    // Starred properties first
    const aFav = a.is_favorite ? 1 : 0;
    const bFav = b.is_favorite ? 1 : 0;
    if (bFav !== aFav) return bFav - aFav;
    return (a.property_name || "").localeCompare(b.property_name || "");
  });
  return grouped;
}
